import React, { useState, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { toast } from 'react-hot-toast'
import { 
  CloudArrowUpIcon, 
  PhotoIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'

interface UploadItem {
  id: string
  file: File
  preview: string
  status: 'pending' | 'uploading' | 'completed' | 'error'
  progress: number
  error?: string
}

interface BulkUploadProps {
  onComplete?: () => void
}

export default function BulkUpload({ onComplete }: BulkUploadProps) {
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([])
  const [dragActive, setDragActive] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadSessionId, setUploadSessionId] = useState<string>('')
  const { user } = useAuth()

  const generateId = () => Math.random().toString(36).substr(2, 9)
  
  const generateUUID = () => {
    // Fallback UUID generation for browsers that don't support crypto.randomUUID()
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID()
    }
    // Fallback implementation
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0
      const v = c === 'x' ? r : ((r & 0x3) | 0x8)
      return v.toString(16)
    })
  }

  const addFiles = useCallback((files: File[]) => {
    // Generate upload session ID if this is the first batch of files
    if (uploadItems.length === 0) {
      setUploadSessionId(generateUUID())
    }
    
    const newItems: UploadItem[] = files.map(file => ({
      id: generateId(),
      file,
      preview: URL.createObjectURL(file),
      status: 'pending',
      progress: 0
    }))

    setUploadItems(prev => [...prev, ...newItems])
  }, [uploadItems.length])

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    )
    
    if (files.length > 0) {
      addFiles(files)
    }
  }, [addFiles])

  const removeItem = (id: string) => {
    setUploadItems(prev => {
      const item = prev.find(item => item.id === id)
      if (item && item.preview) {
        URL.revokeObjectURL(item.preview)
      }
      return prev.filter(item => item.id !== id)
    })
  }

  const uploadFile = async (item: UploadItem): Promise<void> => {
    if (!user) throw new Error('User not authenticated')

    // Update status to uploading
    setUploadItems(prev => prev.map(i => 
      i.id === item.id ? { ...i, status: 'uploading', progress: 0 } : i
    ))

    try {
      // Generate unique filename
      const timestamp = Date.now()
      const randomString = Math.random().toString(36).substring(2, 15)
      const fileExtension = item.file.name.split('.').pop() || 'jpg'
      const filename = `${user.id}/${timestamp}_${randomString}.${fileExtension}`

      // Upload to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('cookie-cutters')
        .upload(filename, item.file, {
          upsert: false
        })

      if (uploadError) {
        throw uploadError
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('cookie-cutters')
        .getPublicUrl(uploadData.path)

      // Update progress to 50% after upload
      setUploadItems(prev => prev.map(i => 
        i.id === item.id ? { ...i, progress: 50 } : i
      ))

      // Create pending cookie cutter record
      const { error: dbError } = await supabase
        .from('pending_cookie_cutters')
        .insert({
          user_id: user.id,
          upload_session_id: uploadSessionId,
          photo_url: publicUrl,
          status: 'uploaded',
          suggested_name: item.file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' '),
          suggested_category: null,
          suggested_bin_id: null,
          created_at: new Date().toISOString()
        })

      if (dbError) {
        throw dbError
      }

      // Mark as completed
      setUploadItems(prev => prev.map(i => 
        i.id === item.id ? { ...i, status: 'completed', progress: 100 } : i
      ))

    } catch (error: any) {
      console.error('Upload error:', error)
      setUploadItems(prev => prev.map(i => 
        i.id === item.id ? { 
          ...i, 
          status: 'error', 
          progress: 0,
          error: error.message 
        } : i
      ))
    }
  }

  const startBulkUpload = async () => {
    if (uploadItems.length === 0) return

    setUploading(true)
    
    try {
      // Upload files sequentially to avoid overwhelming the server
      for (const item of uploadItems) {
        if (item.status === 'pending') {
          await uploadFile(item)
        }
      }
      
      toast.success(`Successfully uploaded ${uploadItems.filter(i => i.status === 'completed').length} photos`)
      
      // Call completion callback after a short delay
      setTimeout(() => {
        if (onComplete) onComplete()
        clearUploadItems()
      }, 1500)
      
    } catch (error) {
      console.error('Bulk upload error:', error)
      toast.error('Bulk upload failed')
    } finally {
      setUploading(false)
    }
  }

  const clearUploadItems = () => {
    uploadItems.forEach(item => {
      if (item.preview) {
        URL.revokeObjectURL(item.preview)
      }
    })
    setUploadItems([])
    setUploadSessionId('')
  }

  const retryFailedUploads = async () => {
    const failedItems = uploadItems.filter(item => item.status === 'error')
    
    setUploading(true)
    for (const item of failedItems) {
      await uploadFile(item)
    }
    setUploading(false)
  }

  const getStatusColor = (status: UploadItem['status']) => {
    switch (status) {
      case 'pending': return 'bg-gray-100 text-gray-800'
      case 'uploading': return 'bg-blue-100 text-blue-800'
      case 'completed': return 'bg-green-100 text-green-800'
      case 'error': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const pendingCount = uploadItems.filter(i => i.status === 'pending').length
  const completedCount = uploadItems.filter(i => i.status === 'completed').length
  const errorCount = uploadItems.filter(i => i.status === 'error').length

  return (
    <div className="bg-white rounded-lg shadow-lg border p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <CloudArrowUpIcon className="h-6 w-6 text-indigo-600" />
        <h2 className="text-lg font-semibold text-gray-900">Bulk Photo Upload</h2>
      </div>

      {/* Drop Zone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive 
            ? 'border-indigo-400 bg-indigo-50' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
        <div className="mt-4">
          <p className="text-sm text-gray-600">
            Drag and drop photos here, or{' '}
            <label className="text-indigo-600 hover:text-indigo-500 cursor-pointer font-medium">
              browse files
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => {
                  const files = Array.from(e.target.files || [])
                  if (files.length > 0) {
                    addFiles(files)
                  }
                }}
                className="hidden"
              />
            </label>
          </p>
          <p className="text-xs text-gray-500 mt-1">
            JPG, PNG, GIF up to 10MB each
          </p>
        </div>
      </div>

      {/* Upload Queue */}
      {uploadItems.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-900">
              Upload Queue ({uploadItems.length} items)
            </h3>
            <div className="flex gap-2">
              {errorCount > 0 && (
                <button
                  onClick={retryFailedUploads}
                  disabled={uploading}
                  className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                >
                  Retry Failed ({errorCount})
                </button>
              )}
              <button
                onClick={clearUploadItems}
                disabled={uploading}
                className="text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-4 mb-4 text-sm">
            <span className="text-gray-600">Pending: {pendingCount}</span>
            <span className="text-green-600">Completed: {completedCount}</span>
            {errorCount > 0 && <span className="text-red-600">Failed: {errorCount}</span>}
          </div>

          {/* Upload Items Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
            {uploadItems.map((item) => (
              <div key={item.id} className="relative group">
                <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={item.preview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Status Overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    {item.status === 'uploading' && (
                      <div className="text-center">
                        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                        <div className="text-white text-xs">{item.progress}%</div>
                      </div>
                    )}
                    {item.status === 'completed' && (
                      <CheckCircleIcon className="h-8 w-8 text-green-400" />
                    )}
                    {item.status === 'error' && (
                      <ExclamationTriangleIcon className="h-8 w-8 text-red-400" />
                    )}
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => removeItem(item.id)}
                    disabled={item.status === 'uploading'}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </div>

                {/* Status Badge */}
                <div className="mt-1">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                    {item.status === 'pending' ? 'Ready' : item.status}
                  </span>
                </div>

                {/* File Name */}
                <div className="text-xs text-gray-500 truncate mt-1" title={item.file.name}>
                  {item.file.name}
                </div>

                {/* Error Message */}
                {item.error && (
                  <div className="text-xs text-red-600 mt-1 truncate" title={item.error}>
                    {item.error}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Upload Controls */}
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {pendingCount > 0 && `${pendingCount} photos ready to upload`}
              {completedCount > 0 && ` • ${completedCount} uploaded`}
              {errorCount > 0 && ` • ${errorCount} failed`}
            </div>
            <button
              onClick={startBulkUpload}
              disabled={pendingCount === 0 || uploading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CloudArrowUpIcon className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : `Upload ${pendingCount} Photos`}
            </button>
          </div>

          {/* Upload in Progress Notice */}
          {uploading && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mr-2"></div>
                <p className="text-sm text-blue-800">
                  Uploading photos... Please don't close this page.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}